import { useSyncExternalStore, useEffect } from "react";
import { formatEther } from "ethers";
import { useAccount } from "wagmi";
import { getProvider } from "@/lib/contracts";
import { createStore } from "@/lib/externalStore";

/**
 * 当前连接账户的 ETH 余额，只在账户变化与交易完成后拉取，不做定时轮询。
 *
 * 直接读取项目配置的 RPC（Robinhood Chain），
 * 避免不同钱包 provider 指向不同 RPC 导致余额为 0 的问题。
 */
const store = createStore<string | null>(null);

let currentAccount: string | null = null;
let inflight: Promise<void> | null = null;

export async function refreshEthBalance(): Promise<void> {
  const account = currentAccount;
  if (!account) {
    store.set(null);
    return;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const wei = await getProvider().getBalance(account);
      store.set(formatEther(wei));
    } catch (err) {
      console.error("[useEthBalance] getBalance failed:", err);
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function useEthBalance() {
  const { address, isConnected } = useAccount();
  const balance = useSyncExternalStore(store.subscribe, store.get);

  useEffect(() => {
    const next = isConnected && address ? address : null;
    if (next !== currentAccount) {
      currentAccount = next;
      refreshEthBalance();
    }
  }, [address, isConnected]);

  return balance;
}
